import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, SlidersHorizontal } from "lucide-react";
import { getTours } from "../lib/adventureApi";
import RALayout from "../components/ra/RALayout";
import { useSeo } from "../lib/seo";
import TourCard from "../components/ra/TourCard";

const CATS = [
  { id: "", label: "All" },
  { id: "trekking", label: "Trekking" },
  { id: "wellness", label: "Wellness" },
  { id: "motorcycle", label: "Motorcycle" },
  { id: "cycling", label: "Cycling" },
  { id: "flying", label: "Flying" },
  { id: "culture", label: "Cultural" },
];
const DIFFS = ["All", "Easy", "Moderate", "Challenging", "Strenuous"];

export default function Tours() {
  useSeo({ title: "All tours | Responsible Adventures", description: "Browse small-group Himalayan adventures: trekking, wellness, cycling, motorcycle, flying and cultural journeys.", path: "/tours" });
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const destination = params.get("destination") || "";
  const [tours, setTours] = useState(null);
  const [diff, setDiff] = useState("All");

  useEffect(() => {
    setTours(null);
    const q = {};
    if (category) q.category = category;
    if (destination) q.destination = destination;
    getTours(q).then(setTours).catch(() => setTours([]));
  }, [category, destination]);

  const setCategory = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set("category", id); else next.delete("category");
    setParams(next);
  };

  const filtered = useMemo(() => {
    if (!tours) return [];
    return diff === "All" ? tours : tours.filter((t) => (t.difficulty || "").toLowerCase().includes(diff.toLowerCase()));
  }, [tours, diff]);

  return (
    <RALayout>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-12">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-moss mb-3">
          {destination === "tibet" ? "Tibet" : "Nepal"} · Responsible Adventures
        </p>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight" data-testid="tours-title">
          {category ? CATS.find((c) => c.id === category)?.label + " Tours" : "All Tours"}
        </h1>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="tours-filters">
          {CATS.map((c) => (
            <button key={c.id || "all"} onClick={() => setCategory(c.id)} data-testid={`filter-cat-${c.id || "all"}`}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${category === c.id ? "bg-terracotta text-white border-terracotta" : "bg-white border-sand-dark text-ink-secondary hover:border-terracotta"}`}>
              {c.label}
            </button>
          ))}
          <span className="mx-2 h-5 w-px bg-sand-dark hidden sm:block" />
          <span className="flex items-center gap-1.5 text-ink-muted text-xs font-mono uppercase tracking-widest"><SlidersHorizontal className="w-3.5 h-3.5" /></span>
          {DIFFS.map((d) => (
            <button key={d} onClick={() => setDiff(d)} data-testid={`filter-diff-${d.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${diff === d ? "bg-moss text-white border-moss" : "bg-white border-sand-dark text-ink-muted hover:border-moss"}`}>
              {d}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {tours === null ? (
          <div className="flex items-center text-ink-secondary py-20"><Loader2 className="w-5 h-5 animate-spin mr-2 text-terracotta" /> Loading tours…</div>
        ) : filtered.length === 0 ? (
          <p className="text-ink-muted py-20" data-testid="tours-empty">No tours match these filters.</p>
        ) : (
          <>
            <p className="font-mono text-xs text-ink-muted mb-5" data-testid="tours-count">{filtered.length} tours</p>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="tours-grid">
              {filtered.map((t, i) => <TourCard key={t.slug} tour={t} index={i} />)}
            </div>
          </>
        )}
      </section>
    </RALayout>
  );
}
